import { useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useEffectOnce, useSecondOrderCallback } from '@chilecompra/react-kit'

import { CLAIM_TYPES } from '../../config/settings/constants'
import { REACT_APP_MERCADO_PUBLICO_BASE_URL } from '../../config/settings/environment.config'
import { getCookieFromDomain } from '../../utils'
import { onShowSnackbar } from '../SnackbarProvider'
import {
  onAddFileThunk,
  onCreateClaimThunk,
  onGetClaimReasonsByTypeIdThunk,
  onGetClaimTypesThunk,
  onGetOrganizationByDniThunk,
  onGetOrganizationByProcessCodeThunk,
  onRemoveFileSuccess,
  onResetFormErrors,
  onResetOrganization,
  onResetOrganizationOrgName
} from './Claims.actions'
import { Root } from './Claims.styles'
import ClaimsActions from './components/ClaimsActions'
import ClaimsForm from './components/ClaimsForm'
import ClaimsHeader from './components/ClaimsHeader'
import ClaimsSuccess from './components/ClaimsSuccess'

const isSameFile = (attached, file) =>
  attached.fileName === file.name && attached.file.size === file.size

/**
 * The Claims container.
 */
const Claims = () => {
  const dispatch = useDispatch()
  const formRef = useRef(null)

  const { attachedFiles, claimReasons, claimTypes, organization, submit } = useSelector(
    state => state.claims
  )

  const userInfo = getCookieFromDomain('user_info')
  const userInfoData = Object.fromEntries(new URLSearchParams(userInfo))
  const ip = userInfoData?.host?.split(',')[0].trim() || ''

  const isFormDisabled =
    Boolean(claimTypes.error) ||
    claimReasons.loading ||
    claimTypes.loading ||
    organization.loading ||
    organization.loadingOrgName ||
    submit.loading

  const notify = useCallback(
    (message, severity = 'warning') => dispatch(onShowSnackbar({ message, severity })),
    [dispatch]
  )

  const handleBack = () => {
    window.location.href = `${REACT_APP_MERCADO_PUBLICO_BASE_URL}/Portal/Modules/Menu/Menu.aspx`
  }

  const handleAddFile = useCallback(
    file => {
      if (attachedFiles.some(attached => isSameFile(attached, file))) {
        return notify('El archivo ya fue adjuntado.')
      }

      dispatch(onAddFileThunk({ file }))
    },
    [attachedFiles, notify, dispatch]
  )

  const handleClaimTypeChange = useCallback(
    typeId => {
      if (typeId === CLAIM_TYPES.PROCUREMENT_IRREGULARITY && !claimReasons.data.length) {
        dispatch(onGetClaimReasonsByTypeIdThunk())
      }
    },
    [claimReasons.data.length, dispatch]
  )

  const handleRemoveFile = useSecondOrderCallback((_, id) => dispatch(onRemoveFileSuccess({ id })))

  const handleResetFormErrors = useCallback(() => dispatch(onResetFormErrors()), [dispatch])

  const handleResetOrganization = useCallback(() => dispatch(onResetOrganization()), [dispatch])

  const handleResetOrganizationOrgName = useCallback(
    () => dispatch(onResetOrganizationOrgName()),
    [dispatch]
  )

  const handleSearchOrganizationByProcessCode = useCallback(
    processCode => {
      if (!processCode) return

      if (!organization.disabledIfFound) formRef.current?.resetOrganizationDni()

      dispatch(onGetOrganizationByProcessCodeThunk({ processCode }))
    },
    [dispatch, organization.disabledIfFound]
  )

  const handleSearchOrganizationByDni = useCallback(
    dni => {
      if (!dni || organization.disabledIfFound) return

      dispatch(onGetOrganizationByDniThunk({ dni }))
    },
    [dispatch, organization.disabledIfFound]
  )

  const handleSubmit = useCallback(() => {
    if (!formRef.current?.validate()) return

    dispatch(
      onCreateClaimThunk({
        attachedFiles,
        callbackFormReset: () => formRef.current?.reset(),
        claim: formRef.current.getValues(),
        ip
      })
    )
  }, [attachedFiles, dispatch, ip])

  useEffectOnce(() => {
    dispatch(onGetClaimTypesThunk())
  })

  return (
    <Root direction="column" spacing={5}>
      <ClaimsHeader />

      {!submit.success && (
        <>
          <ClaimsForm
            attachedFiles={attachedFiles}
            claimReasons={claimReasons}
            claimTypes={claimTypes}
            onAddFile={handleAddFile}
            onClaimTypeChange={handleClaimTypeChange}
            onRemoveFile={handleRemoveFile}
            onResetFormErrors={handleResetFormErrors}
            onResetOrganization={handleResetOrganization}
            onResetOrganizationOrgName={handleResetOrganizationOrgName}
            onSearchOrganizationByDni={handleSearchOrganizationByDni}
            onSearchOrganizationByProcessCode={handleSearchOrganizationByProcessCode}
            organization={organization}
            ref={formRef}
            submitError={Boolean(submit.error)}
            submitLoading={submit.loading}
          />

          <ClaimsActions
            isDisabled={isFormDisabled}
            onBack={handleBack}
            onSubmit={handleSubmit}
            submitError={Boolean(submit.error)}
          />
        </>
      )}

      {submit.success && <ClaimsSuccess code={submit.code} />}
    </Root>
  )
}

export default Claims
